import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { MediaFile } from './MediaFile'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { insertImage } from './insertImage'
import { useCompleteDesignAction } from '../utils/useCompleteDesignAction'
import { DesignActionType } from 'types'
import { IMediaFile } from './Types'
import { track } from '@tldraw/tldraw'
import { getCurrentFrame } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { useInsertMediaFile } from '../utils/useInsertMediaFile'
import { isVideo } from 'components/ImagePickerModal/utils'
import { insertVideo } from './insertVideo'
import { createBackground } from '../BackgroundsTab/createBackground'
import { useUploadsTab } from './UploadsTabContext'
import { useUploadsPanelUploadsList } from './UploadsPanelListContext'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSelection } from 'providers/SelectionProvider/SelectionProvider'
import { Checkbox } from 'blaze-ui/Checkbox'
import { useHover } from '@react-aria/interactions'
import cx from 'classnames'
import Styles from './UploadsTab.module.scss'

export const UploadedFile = track(
  observer(
    ({ file, size }: { file: IMediaFile & { media_file_id?: number }; size?: { height: number; width: number } }) => {
      const { designStore, drawersStore } = useRootStore()
      const { isMobile } = useResponsiveDesign()
      const editor = useEditor()
      const { forBackground } = useUploadsTab()
      const isUploadsPanelUploadsList = useUploadsPanelUploadsList()
      const { toggle, isSelected, hasSelection } = useSelection()
      const { isHovered, hoverProps } = useHover({})
      const completeActionMutation = useCompleteDesignAction()
      const insertMediaFile = useInsertMediaFile()
      const { source_url, thumbnail_url } = file
      const src = thumbnail_url || source_url
      const isVideoFile = isVideo({ type: file.mime_type })

      const mediaFileId = file.media_file_id

      const handleInsert = async () => {
        if (isVideoFile) {
          insertVideo(editor, file)
          drawersStore.closeDrawer()
          return
        }

        if (forBackground && file.width && file.height) {
          createBackground(editor, {
            src: file.source_url,
            w: file.width,
            h: file.height,
            mimeType: file.mime_type || 'image/png',
          })
          if (isMobile) drawersStore.resetSnap()

          return
        }

        if (file.mime_type === 'image/svg+xml') {
          insertImage(editor, file)
          completeActionMutation.mutate({ action: DesignActionType.CoreImage })
          drawersStore.closeDrawer()
          return
        }

        designStore?.setProcessingShapeId(getCurrentFrame(editor).id)
        await insertMediaFile(
          editor,
          {
            mediaFile: file,
            transformations: {},
          },
          {
            replaceReplaceable: true,
          },
        )
        completeActionMutation.mutate({ action: DesignActionType.CoreImage })
        designStore?.setProcessingShapeId(undefined)
        drawersStore.closeDrawer()
      }

      const media = (
        <MediaFile
          key={file.id}
          aria-label={`Add this ${isVideoFile ? 'video' : 'photo'} to the canvas`}
          src={src}
          file={file}
          onPress={handleInsert}
          payload={{ special_type: 'media_file' }}
          size={size}
          disableDrag={hasSelection}
        />
      )

      if (!isUploadsPanelUploadsList || mediaFileId == null) {
        return media
      }

      const selectableMediaFileId = mediaFileId
      const selected = isSelected(selectableMediaFileId)

      const handleCheckboxChange = () => {
        toggle(selectableMediaFileId)
      }

      const handleThumbnailPress = async () => {
        if (hasSelection) return
        await handleInsert()
      }

      const handleSelectableCellClick = (e: React.MouseEvent) => {
        if (!hasSelection) return
        const el = e.target as HTMLElement
        if (el.closest('[data-uploads-checkbox]')) return
        toggle(selectableMediaFileId)
      }

      const bulkListMedia = (
        <MediaFile
          key={file.id}
          aria-label={
            hasSelection
              ? `${selected ? 'Deselect' : 'Select'} this ${isVideoFile ? 'video' : 'photo'}`
              : `Add this ${isVideoFile ? 'video' : 'photo'} to the canvas`
          }
          src={src}
          file={file}
          onPress={handleThumbnailPress}
          payload={{ special_type: 'media_file' }}
          size={size}
          disableDrag={hasSelection}
        />
      )

      return (
        <div
          className={cx(Styles.uploadsFileCell, {
            [Styles.withSelection]: hasSelection,
            [Styles.hovered]: isHovered,
            [Styles.selected]: selected,
            [Styles.uploadsFileCellSelectable]: hasSelection,
          })}
          onClick={handleSelectableCellClick}
          {...hoverProps}
        >
          <div
            data-uploads-checkbox
            className={Styles.uploadsCheckboxContainer}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Checkbox.Label
              className={Styles.uploadsCheckboxLabel}
              isSelected={isSelected(selectableMediaFileId)}
              onChange={handleCheckboxChange}
              aria-label={`Select upload ${file.title ?? file.id}`}
            >
              <Checkbox.Box color="var(--selected-stroke)" className={Styles.uploadsCheckbox} />
            </Checkbox.Label>
          </div>
          {bulkListMedia}
        </div>
      )
    },
  ),
)
